import { FC } from "react";
import { HalfEvent, MatchPhone } from "refsix-js-models";
import { useTranslation } from "react-i18next";
import { IonIcon, IonItem, IonText } from "@ionic/react";
import { lockClosed } from "ionicons/icons";
import RoundBarComponent from "./RoundBarComponent";
import SpeedComponent from "./SpeedComponent";
import mapEventNameToDisplayName from "../utils/MapEventToDisplayName";
import { hasFeatureAccess } from "../services/subscriptionService";
import { ProTeaser } from "./ProTeaser";
import { GREEN_GRADIENT } from "../constants/StatsColorPalette";

export interface SpeedCategoryComponentInterface {
    match: MatchPhone;
    playedSegments: HalfEvent[];
}

const SpeedCategoryComponent: FC<SpeedCategoryComponentInterface> = ({
    match,
    playedSegments,
}) => {
    const { t } = useTranslation();
    const speedThroughMatchAccess = hasFeatureAccess("speedThroughMatch");

    if (!match.stats) {
        return <></>;
    }

    const speedCategoryDistances = [
        match.stats.speedCategoryDistances[1],
        match.stats.speedCategoryDistances[2],
        match.stats.speedCategoryDistances[3],
        match.stats.speedCategoryDistances[4] +
            match.stats.speedCategoryDistances[5],
    ];

    const speedCategoryDurations = [
        match.stats.speedCategoryDurations[0],
        match.stats.speedCategoryDurations[1],
        match.stats.speedCategoryDurations[2],
        match.stats.speedCategoryDurations[3],
        match.stats.speedCategoryDurations[4] +
            match.stats.speedCategoryDurations[5],
    ];

    const series = [
        t("graphs.SpeedCategoryDistanceGraph.stand"),
        t("general.walk"),
        t("general.jog"),
        t("general.run"),
        t("general.sprint"),
    ];

    // Only the array matching the match's own period format is populated. A
    // match whose stats predate top speed has none of them until the
    // recalculation middleware catches up, hence the fallback.
    function periodTopSpeeds(): number[] {
        const stats = match.stats;
        if (!stats) return [];

        switch (match.periodsNo) {
            case "3":
                return stats.speedMaxByThirds || [];
            case "4":
                return stats.speedMaxByQuarters || [];
            default:
                return stats.speedMaxByHalves || [];
        }
    }

    return (
        <>
            <IonItem lines="none">
                <IonText>{t("stats.stats.Speed.topSpeed")}</IonText>
                <IonText slot="end" data-testid="top-speed">
                    <SpeedComponent
                        speedInMetersPerSecond={match.stats.speedMax || 0}
                    />
                </IonText>
            </IonItem>

            {playedSegments.length > 1 &&
                playedSegments.map((segment, index) => (
                    <IonItem key={index} lines="none">
                        <IonText slot="start">
                            {mapEventNameToDisplayName(segment, t)}
                        </IonText>
                        <IonText
                            slot="end"
                            data-testid={`top-speed-period-${index}`}
                        >
                            {speedThroughMatchAccess ? (
                                <SpeedComponent
                                    speedInMetersPerSecond={
                                        periodTopSpeeds()[index] || 0
                                    }
                                />
                            ) : (
                                <IonIcon icon={lockClosed} />
                            )}
                        </IonText>
                    </IonItem>
                ))}

            <ProTeaser
                showTeaser={
                    !speedThroughMatchAccess && match._id !== "example-result1"
                }
                teaserName="stats-speed-distance"
            >
                <RoundBarComponent
                    title={t("fixture.FixtureSummaryStats.distanceCovered")}
                    values={speedCategoryDistances}
                    series={series.slice(1, 5)}
                    distanceBy="distance"
                    numberOfCategories={4}
                    unit={"distance"}
                    colorOverride={GREEN_GRADIENT}
                    useDummyData={
                        !speedThroughMatchAccess &&
                        match._id !== "example-result1"
                    }
                />
            </ProTeaser>

            <ProTeaser
                showTeaser={
                    !speedThroughMatchAccess && match._id !== "example-result1"
                }
                teaserName="stats-speed-percentage"
            >
                <RoundBarComponent
                    title={t("fixture.FixtureSummaryStats.timeDuration")}
                    values={speedCategoryDurations}
                    series={series}
                    distanceBy="duration"
                    numberOfCategories={5}
                    unit={"%"}
                    colorOverride={GREEN_GRADIENT}
                    useDummyData={
                        !speedThroughMatchAccess &&
                        match._id !== "example-result1"
                    }
                />
            </ProTeaser>
        </>
    );
};

export default SpeedCategoryComponent;
